import { Fragment } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  required,
  styled,
  ToucanButton,
  ToucanChoice,
  ToucanFilePicker,
} from "toucan";
import { AlbumResult } from "../components/AlbumResult.tsx";
import { SearchDialog } from "../components/SearchDialog.tsx";
import {
  SOURCES,
  addChosen,
  findLeftover,
  importAlbumPlaylists,
  importLiked,
  importPastedList,
  pickCandidate,
  showCandidateOptions,
  takeSearchResult,
  toggleCandidate,
} from "../import.ts";
import type { Candidate, ImportOutcome, Leftover } from "../import.ts";
import { defaultShelfId, shelfOptions } from "../library.ts";
import { SURE_SCORE, UNSHELVED_LABEL } from "../model.ts";
import type { FoundAlbum } from "../model.ts";
import { go } from "../routes.ts";
import type { ImportSource } from "../routes.ts";
import { pacedStopped, stopPaced } from "../spotify.ts";
import { shelves, store } from "../state.ts";

/**
 * The chooser groups what Spotify already has apart from what you bring to it.
 * Two of the three sources are Spotify reading its own account, which is not
 * apparent from their names alone.
 */
const GROUPS: { heading: string; sources: ImportSource[] }[] = [
  { heading: "From your Spotify account", sources: ["liked", "playlists"] },
  { heading: "From a list of your own", sources: ["list"] },
];

/**
 * The blurb each source screen opens with. Opening the screen starts the two
 * that read from Spotify, so they say what they are reading rather than
 * offering a button to say it again; a pasted list has nothing to read until
 * it has been pasted, so it has both.
 */
const BLURBS: Record<ImportSource, string> = {
  liked:
    "Every album saved to your Spotify library. Exact: the ids come back with the albums, so nothing has to be matched or confirmed.",
  playlists:
    "Playlists you made whose tracks all belong to one album — Spotify's API cannot see playlist folders, so an album kept in one is found by its shape.",
  list: "One album per line: “Artist — Album”, “Album by Artist”, a Spotify link, or a folder listing like “Artist/Album”.",
};

const Page = styled("section")`
  min-height: 100%;
  padding: 1rem;
  background: var(--toucan-canvas);

  .note {
    margin: 0;
    color: var(--toucan-muted);
  }

  h3 {
    margin: 0.5rem 0 0;
    font-size: 1.125rem;
  }

  .sources {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .sources textarea {
    margin: 0;
  }

  /* The chooser an import opens on: one source per row, each its own screen. */
  .choices {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  /* A running import: what it is doing, how long is left, and Stop on the
     same line, since the pacing makes an import slow. */
  .status {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
  }

  .progress {
    margin: 0;
    color: var(--toucan-ink);
  }

  .left {
    color: var(--toucan-muted);
  }

  .candidates,
  .leftovers {
    list-style: none;
    margin: 0.75rem 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .candidates li::marker,
  .leftovers li::marker,
  .options li::marker {
    content: none;
  }

  /* Where a candidate came from — the pasted line, or the playlist it was in. */
  .source {
    display: block;
    color: var(--toucan-muted);
    margin-bottom: 0.15rem;
  }

  /* The album on the left, what else the search offered on the right — or
     under it, on a phone too narrow for both, rather than the album's name
     squeezed to a letter a line. */
  .row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .row > button:first-child {
    flex: 1 1 14rem;
    min-width: 0;
  }

  .more {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  /* Hand this line to the album search instead: last resort under the
     alternatives, and the only offer on a line nothing matched. */
  .search {
    flex: 0 0 auto;
    margin-top: 0.25rem;
    white-space: nowrap;
  }

  .options {
    list-style: none;
    margin: 0.25rem 0 0;
    padding: 0 0 0 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .missed,
  .held,
  .leftover {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border: 1px dashed var(--toucan-border-strong);
    border-radius: var(--pico-border-radius);
    color: var(--toucan-muted);
  }

  .leftover-name {
    flex: 1 1 auto;
  }

  .reason {
    color: var(--toucan-muted);
    font-style: italic;
  }

  .missed .label,
  .held .label {
    flex: 1 1 auto;
  }
`;

/** A candidate and where it sits in the review, so a row can act on it. */
interface Row {
  candidate: Candidate;
  index: number;
}

/**
 * The album a candidate's line found, where the library holds it already.
 * Adding would change nothing, so the review says so instead of counting it.
 */
function heldAlbum(candidate: Candidate): FoundAlbum | null {
  const found = candidate.found;
  if (found === null) {
    return null;
  }
  return store().albums.some((album) => album.ids.spotify === found.id)
    ? found
    : null;
}

/**
 * How many albums pressing Add would bring in: the ticked matches the library
 * lacks, each once — two lines that found one record are one record.
 */
function countChosen(candidates: Candidate[]): number {
  const ids = new Set<string>();
  for (const candidate of candidates) {
    if (
      candidate.chosen &&
      candidate.found !== null &&
      heldAlbum(candidate) === null
    ) {
      ids.add(candidate.found.id);
    }
  }
  return ids.size;
}

/** What the review can do to one of its rows. */
interface RowActions {
  onToggle: (index: number) => void;
  onOptions: (index: number) => void;
  onPick: (index: number, option: number) => void;
  onSearch: (index: number) => void;
}

/** One line of the review, with what was asked for and what answered it. */
function CandidateRow({
  candidate,
  index,
  actions,
}: {
  candidate: Candidate;
  index: number;
  actions: RowActions;
}) {
  if (candidate.found === null) {
    return (
      <li class="missed">
        <span class="label">{candidate.label}</span>
        <ToucanButton
          class="search"
          onClick={() => {
            actions.onSearch(index);
          }}
        >
          Search for it
        </ToucanButton>
      </li>
    );
  }
  const held = heldAlbum(candidate);
  if (held !== null) {
    return (
      <li class="held">
        <span class="label">{candidate.label}</span>
        <span class="reason">
          {held.name} — {held.artist}
        </span>
      </li>
    );
  }
  return (
    <li>
      <span class="source">{candidate.label}</span>
      <div class="row">
        <AlbumResult
          name={candidate.found.name}
          artist={candidate.found.artist}
          image={candidate.found.image}
          chosen={candidate.chosen}
          onPick={() => {
            actions.onToggle(index);
          }}
        />
        {candidate.options.length > 0 && (
          <ToucanButton
            class="more"
            onClick={() => {
              actions.onOptions(index);
            }}
          >
            {candidate.showing
              ? "Hide"
              : `Other matches (${String(candidate.options.length)})`}
          </ToucanButton>
        )}
      </div>
      {/* The right album is second often enough that a wrong match is a tap to
          fix, and the alternatives are noise on a row until they are asked
          for. */}
      {candidate.showing && (
        <>
          <ul class="options">
            {candidate.options.map((album, option) => (
              <li key={album.id}>
                <AlbumResult
                  name={album.name}
                  artist={album.artist}
                  image={album.image}
                  onPick={() => {
                    actions.onPick(index, option);
                  }}
                />
              </li>
            ))}
          </ul>
          {/* None of them right either: the line's own words open the album
              search, where they can be edited into something Spotify knows. */}
          <ToucanButton
            class="search"
            onClick={() => {
              actions.onSearch(index);
            }}
          >
            None of these — search
          </ToucanButton>
        </>
      )}
    </li>
  );
}

/** One headed group of the review, or nothing when it is empty. */
function Group({
  heading,
  note,
  rows,
  actions,
}: {
  heading: string;
  note: string;
  rows: Row[];
  actions: RowActions;
}) {
  if (rows.length === 0) {
    return null;
  }
  return (
    <>
      <h3>
        {heading} {rows.length}
      </h3>
      <p class="note">{note}</p>
      <ul class="candidates">
        {rows.map(({ candidate, index }) => (
          <CandidateRow
            key={candidate.label + String(index)}
            candidate={candidate}
            index={index}
            actions={actions}
          />
        ))}
      </ul>
    </>
  );
}

/**
 * Bringing a collection onto the library: the chooser, one screen per source,
 * and the review every source ends at. Arriving at a different screen drops
 * whatever the last one found — a review belongs to the source it came from —
 * which is what mounting one component per source is for.
 */
export function ImportPage({
  source,
  createdShelfId,
  onNewShelf,
  onAdded,
}: {
  source: ImportSource | null;
  createdShelfId: string | null;
  onNewShelf: (suggested: string) => void;
  onAdded: (shelfId: string | null) => void;
}) {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [leftovers, setLeftovers] = useState<Leftover[]>([]);
  const [status, setStatus] = useState("");
  const [left, setLeft] = useState("");
  // The import goes to the shelf the library was left on until the chips say
  // otherwise, because an import started from a shelf is nearly always an
  // import for that shelf.
  const [shelfId, setShelfId] = useState<string | null>(defaultShelfId);
  const [shelfName, setShelfName] = useState("");
  const [text, setText] = useState("");
  const [searchFor, setSearchFor] = useState<number | null>(null);

  const report = (message: string, remaining: string): void => {
    setStatus(message);
    setLeft(remaining);
  };

  const take = (outcome: ImportOutcome | null): void => {
    // A run that failed said so in a toast, and leaves the review it could not
    // replace standing.
    if (outcome !== null) {
      setCandidates(outcome.candidates);
      setLeftovers(outcome.leftovers);
      setShelfName(outcome.shelfName);
    }
  };

  /** Start the source that needs nothing typed in first. */
  const begin = (from: "liked" | "playlists"): void => {
    void (async () => {
      take(
        from === "liked"
          ? await importLiked(report)
          : await importAlbumPlaylists(report),
      );
    })();
  };
  // The screen is mounted fresh per source, so this runs once — through a ref,
  // since the handler it calls is this render's.
  const start = useRef(begin);
  start.current = begin;
  useEffect(() => {
    if (source !== null && source !== "list") {
      start.current(source);
    }
  }, [source]);

  // A shelf made from this screen's "＋ New shelf" chip is where the import
  // was asking to land.
  useEffect(() => {
    if (createdShelfId !== null) {
      setShelfId(createdShelfId);
    }
  }, [createdShelfId]);

  const actions: RowActions = {
    onToggle: (index) => {
      setCandidates(toggleCandidate(candidates, index));
    },
    onOptions: (index) => {
      setCandidates(showCandidateOptions(candidates, index));
    },
    onPick: (index, option) => {
      setCandidates(pickCandidate(candidates, index, option));
    },
    onSearch: setSearchFor,
  };

  const rows = candidates.map((candidate, index) => ({ candidate, index }));
  const chosen = countChosen(candidates);
  /** The rows still to decide, which are matched and not yet in the library. */
  const open = rows.filter(
    ({ candidate }) =>
      candidate.found !== null && heldAlbum(candidate) === null,
  );

  return (
    <Page className="page-column">
      <section class="sources">
        {source === null ? (
          <Chooser />
        ) : (
          <>
            <p class="note">{BLURBS[source]}</p>
            {source === "list" && (
              <>
                <textarea
                  rows={6}
                  aria-label="Albums to import"
                  value={text}
                  onInput={(event) => {
                    setText(event.currentTarget.value);
                  }}
                />
                <ToucanButton
                  onClick={() => {
                    void (async () => {
                      take(await importPastedList(text, report));
                    })();
                  }}
                >
                  Look these up
                </ToucanButton>
              </>
            )}
          </>
        )}
      </section>
      {status !== "" && (
        <div class="status">
          <p class="progress">
            {status}
            {left !== "" && <span class="left"> — {left}</span>}
          </p>
          {/* The run notices a stop once the call it is waiting on comes back,
              which is seconds away at import pace — so the line says it is
              stopping rather than appearing to ignore the button. */}
          <ToucanButton
            disabled={pacedStopped()}
            onClick={() => {
              stopPaced();
              setStatus("Stopping…");
              setLeft("");
            }}
          >
            Stop
          </ToucanButton>
        </div>
      )}
      {/* Sure matches and doubtful ones are kept apart, so a doubtful match
          is never read as a settled one. */}
      {candidates.length > 0 && (
        <section class="review">
          <Group
            heading="Found"
            note="Tap a row to include or leave out."
            rows={open.filter(({ candidate }) => candidate.score >= SURE_SCORE)}
            actions={actions}
          />
          <Group
            heading="Not sure about"
            note="The search was not confident of these. Check each one, or take another match."
            rows={open.filter(({ candidate }) => candidate.score < SURE_SCORE)}
            actions={actions}
          />
          <Group
            heading="Already in the library"
            note="These lines found albums the library holds, so there is nothing to add."
            rows={rows.filter(({ candidate }) => heldAlbum(candidate) !== null)}
            actions={actions}
          />
          <Group
            heading="Nothing found for"
            note="Spotify had no album for these lines."
            rows={rows.filter(({ candidate }) => candidate.found === null)}
            actions={actions}
          />
          {/* Where this import will land: one shelf for the lot. */}
          <ToucanFilePicker
            options={shelfOptions(shelves())}
            chosen={shelfId}
            label="Add to shelf"
            noneLabel={UNSHELVED_LABEL}
            newLabel="＋ New shelf"
            onPick={setShelfId}
            onNew={() => {
              onNewShelf(shelfName);
            }}
          />
          <ToucanButton
            variant="primary"
            disabled={chosen === 0}
            onClick={() => {
              onAdded(addChosen(candidates, shelfId));
            }}
          >
            Add {chosen} to the library
          </ToucanButton>
        </section>
      )}
      {leftovers.length > 0 && (
        <section class="left-behind">
          <h3>Not imported {leftovers.length}</h3>
          <p class="note">
            Playlists that are not one album. Search Spotify by the playlist’s
            name to add one anyway.
          </p>
          <ul class="leftovers">
            {leftovers.map((leftover) => (
              <li key={leftover.name} class="leftover">
                <span class="leftover-name">{leftover.name}</span>
                <span class="reason">{leftover.reason}</span>
                <ToucanButton
                  onClick={() => {
                    void (async () => {
                      const found = await findLeftover(leftover.name, report);
                      if (found !== null) {
                        setCandidates([...candidates, found]);
                        setLeftovers(
                          leftovers.filter((entry) => entry !== leftover),
                        );
                      }
                    })();
                  }}
                >
                  Search for it
                </ToucanButton>
              </li>
            ))}
          </ul>
        </section>
      )}
      {searchFor !== null && (
        <SearchDialog
          heading="Add an album"
          initialQuery={required(candidates[searchFor], "candidate").label}
          onPick={(found) => {
            setCandidates(takeSearchResult(candidates, searchFor, found));
            setSearchFor(null);
          }}
          onClose={() => {
            setSearchFor(null);
          }}
        />
      )}
    </Page>
  );
}

/** One source per row under the heading for where it reads from. */
function Chooser() {
  return (
    <>
      <p class="note">Where are the albums coming from?</p>
      <div class="choices">
        {GROUPS.map((group) => (
          <Fragment key={group.heading}>
            <h3>{group.heading}</h3>
            {group.sources.map((source) => (
              <ToucanChoice
                key={source}
                title={SOURCES[source].title}
                summary={SOURCES[source].summary}
                onClick={() => {
                  go(`import/${source}`);
                }}
              />
            ))}
          </Fragment>
        ))}
      </div>
    </>
  );
}
